import { Component, OnInit, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragEnd } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GridsterModule, GridsterItem, GridsterConfig, GridType, CompactType } from 'angular-gridster2';
import { ThemeFramesEditorComponent } from '../theme-frames-editor/theme-frames-editor.component';
import { PhotoboothService } from '../../services/photobooth.service';
import { StoreService } from '../../services/store.service';
import { STORAGE_DELETE_MESSAGES, classifyStorageUrl, deleteConfirmationFor } from '../../utils/storage-ownership.util';
import {
  isGlobalRoute,
  serializeLayoutForDirtyCheck,
  STANDALONE_HOUSEKEEPING_MESSAGES,
  tileHasArtwork
} from '../../utils/standalone-housekeeping.util';
import { GlobalFrameGroupCopyModalComponent } from './global-frame-group-copy-modal/global-frame-group-copy-modal.component';

export interface ThemeSelectionTile {
  id: string;
  uid?: string;
  x: number;
  y: number;
  rows: number;
  cols: number;
  properties: {
    title: string;
    subTitle: string;
    price: number;
    priceClassicStrip?: number;
    priceMultiCut?: number;
    cutStyleKey: string;
    imageDirectory: string;
    tileType?: 'Signature' | 'Theme' | 'FeaturedFrame';
    isCustomizable?: boolean;
    isCustomized?: boolean;
    featuredThemeId?: string;
    featuredFrameId?: string;
    textPositionX?: number;
    textPositionY?: number;
    titleColor?: string;
    subtitleColor?: string;
    titleFontSize?: number;
    subtitleFontSize?: number;
    titleBold?: boolean;
    titleItalic?: boolean;
    titleUnderline?: boolean;
    subtitleBold?: boolean;
    subtitleItalic?: boolean;
    subtitleUnderline?: boolean;
  };
}

export interface FrameGroupOption {
  themeId: string;
  frameId: string;
  displayName: string;
  isGlobalGroup?: boolean;
}

export interface FrameGroupGroup {
  themeId: string;
  frames: FrameGroupOption[];
  expanded?: boolean;
}

@Component({
  selector: 'app-theme-layout-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, GridsterModule, ThemeFramesEditorComponent, DragDropModule, GlobalFrameGroupCopyModalComponent],
  templateUrl: './theme-layout-editor.component.html',
  styleUrls: ['./theme-layout-editor.component.css']
})
export class ThemeLayoutEditorComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private photoboothService = inject(PhotoboothService);
  private storeService = inject(StoreService);
  private cdr = inject(ChangeDetectorRef);

  readonly housekeepingMessages = STANDALONE_HOUSEKEEPING_MESSAGES;

  // Gridster settings
  options!: GridsterConfig;
  dashboard: ThemeSelectionTile[] = [];

  // Route params
  storeId: string = '';
  deviceId: string = '';
  isGlobal: boolean = false;
  version: string | null = null;

  // Versioning state
  publishedVersions: any[] = [];
  selectedVersion: string = '';
  activePublishedVersion: string | null = null;
  isTesting: boolean = false;
  isTestingSaved: boolean = false;

  // Component states
  isLoading: boolean = true;
  activeItem: ThemeSelectionTile | null = null;
  isFramesEditorOpen: boolean = false;
  isUploadingThumbnail: boolean = false;

  // Standalone Layout Mode
  overrideGlobalLayout: boolean = false;
  get isFullEditorUnlocked(): boolean {
    return this.isGlobal || this.overrideGlobalLayout;
  }

  thumbnailPreview: { name: string, url: string } | null = null;
  isLoadingThumbnail: boolean = false;
  tileThumbnails: { [tileId: string]: string } = {};

  backgroundPreview: { name: string, url: string } | null = null;
  isLoadingBackground: boolean = false;
  isUploadingBackground: boolean = false;

  allFrameGroupsGrouped: FrameGroupGroup[] = [];
  deviceFrameGroupThemeIds: string[] = [];
  isLoadingFrameGroups: boolean = false;

  // KAN-523 AC-3: Standalone tile deletion confirmation modal state
  deleteTileConfirmModal: {
    tile: ThemeSelectionTile;
    frameGroupCount: number;
    storageFileCount: number;
    promptText: string;
  } | null = null;
  isDeletingStandaloneTile: boolean = false;

  // KAN-523 AC-4: Tile ID artwork lock state
  tileIdArtworkBlockedMessage: string | null = null;

  // KAN-523 AC-11: Dirty state tracking & Publish to Device warning modal
  savedLayoutSnapshot: string = '';
  isUnsavedPublishModalOpen: boolean = false;
  isPublishingDevice: boolean = false;

  get hasUnsavedChanges(): boolean {
    if (this.version) return false;
    return serializeLayoutForDirtyCheck(this.dashboard) !== this.savedLayoutSnapshot;
  }

  syncDirtyState(): void {
    if (!this.isGlobal && this.deviceId) {
      this.photoboothService.setHasUnsavedLayoutChanges(this.deviceId, this.hasUnsavedChanges);
    }
  }

  get isFramesEditorGlobal(): boolean {
    if (this.overrideGlobalLayout) {
      return false;
    }
    if (this.activeItem?.properties?.tileType === 'FeaturedFrame' && this.activeItem.properties.featuredThemeId) {
      for (const group of this.allFrameGroupsGrouped) {
        for (const frame of group.frames) {
          if (frame.themeId === this.activeItem.properties.featuredThemeId &&
            frame.frameId === this.activeItem.properties.featuredFrameId) {
            return !!frame.isGlobalGroup;
          }
        }
      }
    }
    return this.isGlobal;
  }

  isGlobalCopyModalOpen: boolean = false;
  isCopyingGlobalGroup: boolean = false;

  // Video Modal State
  isVideoModalOpen: boolean = false;
  introVideos: string[] = [];
  printingVideos: string[] = [];
  excludeGlobalVideos: boolean = false;
  isUpdatingExcludeGlobalVideos: boolean = false;
  isUploadingVideo: boolean = false;

  printingBackgroundPreview: { name: string, url: string } | null = null;
  isLoadingPrintingBackground: boolean = false;
  isUploadingPrintingBackground: boolean = false;

  get canEditProperties(): boolean {
    if (this.version) return false;
    return true;
  }

  originalTileUids: Set<string> = new Set();
  originalCustomizableState: Map<string, boolean> = new Map();

  // Cut styles for configuration dropdown
  cutStyles: string[] = ['2x6 Strip', '4x6 Portrait', '4x6 Landscape', 'Square Grid', 'GIF Animation'];

  get activeItemPixelWidth(): number {
    if (!this.activeItem) return 0;
    const rawW = this.activeItem.cols > 48
      ? this.activeItem.cols
      : this.activeItem.cols * (1920 / 48);
    return Math.max(0, Math.round(rawW) - 30);
  }

  get activeItemPixelHeight(): number {
    if (!this.activeItem) return 0;
    const rawH = this.activeItem.rows > 24
      ? this.activeItem.rows
      : this.activeItem.rows * (1080 / 24);
    return Math.max(0, Math.round(rawH) - 30);
  }

  ngOnInit(): void {
    this.storeId = this.route.snapshot.paramMap.get('storeId') || '';
    this.deviceId = this.route.snapshot.paramMap.get('deviceId') || '';
    this.version = this.route.snapshot.queryParamMap.get('version');
    this.selectedVersion = this.version || '';

    // KAN-523 AC-1: Single shared route predicate (storeId === 'global' && deviceId === 'global')
    this.isGlobal = isGlobalRoute(this.storeId, this.deviceId);

    this.loadPublishedVersions();
    this.initializeGridster();
    this.loadLayout();
  }

  async loadPublishedVersions() {
    try {
      this.publishedVersions = await this.photoboothService.getPublishedVersions();
      this.activePublishedVersion = await this.photoboothService.getActivePublishedVersion();
    } catch (e) {
      console.error('Failed to load published versions', e);
    }
  }

  onVersionChange() {
    this.version = this.selectedVersion || null;

    // Update URL query parameters without reloading the page
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { version: this.version },
      queryParamsHandling: 'merge'
    });

    // Re-initialize layout grid for read-only vs editable rules
    this.initializeGridster();

    // Reload layout from new target
    this.loadLayout();
  }

  // Dragging & Resizing state
  private isDragging = false;
  private isResizing = false;
  private resizeHandle = '';
  private startMouseX = 0;
  private startMouseY = 0;
  private startTileX = 0;
  private startTileY = 0;
  private startTileCols = 0;
  private startTileRows = 0;
  private draggedTile: ThemeSelectionTile | null = null;

  startTileDrag(event: MouseEvent, item: ThemeSelectionTile): void {
    if (this.version || !this.isFullEditorUnlocked) return;
    if ((event.target as HTMLElement)?.closest('.drag-ignore') || (event.target as HTMLElement)?.closest('.resize-handle') || (event.target as HTMLElement)?.closest('.tile-action-btn')) {
      return;
    }

    event.preventDefault();
    this.selectTile(item);

    this.isDragging = true;
    this.draggedTile = item;
    this.startMouseX = event.clientX;
    this.startMouseY = event.clientY;
    this.startTileX = typeof item.x === 'number' ? item.x : 100;
    this.startTileY = typeof item.y === 'number' ? item.y : 100;
    if (!this.draggedTile.cols || this.draggedTile.cols <= 0) this.draggedTile.cols = 400;
    if (!this.draggedTile.rows || this.draggedTile.rows <= 0) this.draggedTile.rows = 600;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging || !this.draggedTile) return;
      const el = document.querySelector('.viewport-inner-16-9');
      const scale = el ? el.clientWidth / 1920 : 1;

      const dx = (e.clientX - this.startMouseX) / scale;
      const dy = (e.clientY - this.startMouseY) / scale;

      this.draggedTile.x = Math.round(Math.max(0, Math.min(1920 - this.draggedTile.cols, this.startTileX + dx)));
      this.draggedTile.y = Math.round(Math.max(0, Math.min(1080 - this.draggedTile.rows, this.startTileY + dy)));
      this.syncDirtyState();
      this.cdr.detectChanges();
    };

    const onMouseUp = () => {
      this.isDragging = false;
      this.draggedTile = null;
      this.syncDirtyState();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      this.cdr.detectChanges();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  startTileResize(event: MouseEvent, item: ThemeSelectionTile, handle: string): void {
    if (this.version || !this.isFullEditorUnlocked) return;
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeHandle = handle;
    this.draggedTile = item;
    this.startMouseX = event.clientX;
    this.startMouseY = event.clientY;
    this.startTileX = typeof item.x === 'number' ? item.x : 100;
    this.startTileY = typeof item.y === 'number' ? item.y : 100;
    this.startTileCols = (typeof item.cols === 'number' && item.cols > 0) ? item.cols : 400;
    this.startTileRows = (typeof item.rows === 'number' && item.rows > 0) ? item.rows : 600;
    this.draggedTile.cols = this.startTileCols;
    this.draggedTile.rows = this.startTileRows;
    this.draggedTile.x = this.startTileX;
    this.draggedTile.y = this.startTileY;

    const minW = 60;
    const minH = 60;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizing || !this.draggedTile) return;
      const el = document.querySelector('.viewport-inner-16-9');
      const scale = el ? el.clientWidth / 1920 : 1;

      const dx = (e.clientX - this.startMouseX) / scale;
      const dy = (e.clientY - this.startMouseY) / scale;

      if (this.resizeHandle.includes('e')) {
        this.draggedTile.cols = Math.round(Math.max(minW, Math.min(1920 - this.draggedTile.x, this.startTileCols + dx)));
      }
      if (this.resizeHandle.includes('s')) {
        this.draggedTile.rows = Math.round(Math.max(minH, Math.min(1080 - this.draggedTile.y, this.startTileRows + dy)));
      }
      if (this.resizeHandle.includes('w')) {
        const maxDeltaX = this.startTileCols - minW;
        const clampedDx = Math.min(dx, maxDeltaX);
        const newX = Math.round(Math.max(0, this.startTileX + clampedDx));
        this.draggedTile.cols = Math.round(this.startTileX + this.startTileCols - newX);
        this.draggedTile.x = newX;
      }
      if (this.resizeHandle.includes('n')) {
        const maxDeltaY = this.startTileRows - minH;
        const clampedDy = Math.min(dy, maxDeltaY);
        const newY = Math.round(Math.max(0, this.startTileY + clampedDy));
        this.draggedTile.rows = Math.round(this.startTileY + this.startTileRows - newY);
        this.draggedTile.y = newY;
      }

      this.syncDirtyState();
      this.cdr.detectChanges();
    };

    const onMouseUp = () => {
      this.isResizing = false;
      this.draggedTile = null;
      this.syncDirtyState();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      this.cdr.detectChanges();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  alignTile(alignment: 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom'): void {
    if (!this.activeItem || this.version || !this.isFullEditorUnlocked) return;
    switch (alignment) {
      case 'left':
        this.activeItem.x = 0;
        break;
      case 'center-h':
        this.activeItem.x = Math.round((1920 - this.activeItem.cols) / 2);
        break;
      case 'right':
        this.activeItem.x = Math.round(1920 - this.activeItem.cols);
        break;
      case 'top':
        this.activeItem.y = 0;
        break;
      case 'center-v':
        this.activeItem.y = Math.round((1080 - this.activeItem.rows) / 2);
        break;
      case 'bottom':
        this.activeItem.y = Math.round(1080 - this.activeItem.rows);
        break;
    }
    this.syncDirtyState();
  }

  clampActiveItem(): void {
    if (!this.activeItem) return;
    this.activeItem.x = Math.max(0, Math.min(1920 - (this.activeItem.cols || 40), this.activeItem.x || 0));
    this.activeItem.y = Math.max(0, Math.min(1080 - (this.activeItem.rows || 40), this.activeItem.y || 0));
    this.activeItem.cols = Math.max(40, Math.min(1920 - this.activeItem.x, this.activeItem.cols || 40));
    this.activeItem.rows = Math.max(40, Math.min(1080 - this.activeItem.y, this.activeItem.rows || 40));
    this.syncDirtyState();
  }

  onCanvasClick(event: MouseEvent): void {
    // Background click handler
  }

  initializeGridster(): void {
    // Unconstrained Free-Form Canvas Editor initialized
  }

  private normalizeTiles(tiles: any[]): ThemeSelectionTile[] {
    return (tiles || []).map(t => {
      if (!t.uid) t.uid = `uid_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;

      // Convert legacy 48x24 grid cell numbers to pixel canvas space ONLY if valid legacy numbers
      if (typeof t.cols === 'number' && typeof t.rows === 'number' && typeof t.x === 'number' && typeof t.y === 'number' &&
        t.cols > 0 && t.rows > 0 && t.cols <= 48 && t.rows <= 24 && t.x <= 48 && t.y <= 24) {
        t.x = Math.round(t.x * (1920 / 48));
        t.y = Math.round(t.y * (1080 / 24));
        t.cols = Math.round(t.cols * (1920 / 48));
        t.rows = Math.round(t.rows * (1080 / 24));
      }

      // Robust pixel defaults if coordinates or dimensions are missing/invalid
      if (t.cols === undefined || t.cols === null || isNaN(t.cols) || t.cols <= 0) t.cols = 400;
      if (t.rows === undefined || t.rows === null || isNaN(t.rows) || t.rows <= 0) t.rows = 600;
      if (t.x === undefined || t.x === null || isNaN(t.x) || t.x < 0) t.x = 100;
      if (t.y === undefined || t.y === null || isNaN(t.y) || t.y < 0) t.y = 100;

      if (t.properties) {
        if (t.properties.imageFileName && !t.properties.imageDirectory) {
          t.properties.imageDirectory = t.properties.imageFileName;
        }
        delete t.properties.imageFileName;
        if (!t.properties.titleColor) t.properties.titleColor = '#ffffff';
        if (!t.properties.subtitleColor) t.properties.subtitleColor = '#e2e8f0';
        if (!t.properties.titleFontSize) t.properties.titleFontSize = 18;
        if (!t.properties.subtitleFontSize) t.properties.subtitleFontSize = 14;
        t.properties.titleBold = !!t.properties.titleBold;
        t.properties.titleItalic = !!t.properties.titleItalic;
        t.properties.titleUnderline = !!t.properties.titleUnderline;
        t.properties.subtitleBold = !!t.properties.subtitleBold;
        t.properties.subtitleItalic = !!t.properties.subtitleItalic;
        t.properties.subtitleUnderline = !!t.properties.subtitleUnderline;
        t.properties.isCustomized = !!t.properties.isCustomized;
      }
      return t as ThemeSelectionTile;
    });
  }

  async loadLayout(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.isGlobal) {
        const rawTiles = await this.photoboothService.getGlobalLayout(this.version);
        this.dashboard = this.normalizeTiles(rawTiles);
      } else {
        const deviceData = await this.storeService.getDevice(this.deviceId);
        if (deviceData) {
          this.isTesting = !!deviceData.isTesting;
        }

        this.overrideGlobalLayout = await this.photoboothService.getDeviceOverrideGlobalLayout(this.deviceId, this.version);

        if (this.overrideGlobalLayout) {
          // Standalone Device Mode: Load device tiles directly without global merging
          const rawDeviceTiles = await this.photoboothService.getDeviceLayout(this.deviceId, this.version);
          this.dashboard = this.normalizeTiles(rawDeviceTiles).map(t => {
            if (!t.properties) t.properties = {} as any;
            t.properties.isCustomized = true;
            return t;
          });
        } else {
          // Standard Global Inheritance Mode: merge customizable tiles onto global baseline
          const globalTiles = await this.photoboothService.getGlobalLayout(this.version);
          const deviceTiles = await this.photoboothService.getDeviceLayout(this.deviceId, this.version);

          const normalizedGlobal = this.normalizeTiles(globalTiles);
          const normalizedDevice = this.normalizeTiles(deviceTiles);

          const deviceTilesMap = new Map<string, ThemeSelectionTile>();
          normalizedDevice.forEach(dt => {
            if (dt.uid) deviceTilesMap.set(dt.uid, dt);
            else if (dt.id) deviceTilesMap.set(dt.id, dt);
          });

          this.dashboard = normalizedGlobal.map(gt => {
            if (gt.properties?.isCustomizable) {
              const dt = deviceTilesMap.get(gt.uid as string) || deviceTilesMap.get(gt.id);
              if (dt && dt.properties?.isCustomized) {
                return {
                  ...gt,
                  properties: {
                    ...gt.properties,
                    ...dt.properties,
                    isCustomized: true
                  }
                };
              } else {
                return {
                  ...gt,
                  properties: {
                    ...gt.properties,
                    isCustomized: false
                  }
                };
              }
            }
            return gt;
          });
        }
      }
      this.originalTileUids = new Set(this.dashboard.map(t => t.uid as string));
      if (this.isGlobal) {
        this.originalCustomizableState.clear();
        this.dashboard.forEach(t => {
          this.originalCustomizableState.set(t.id, !!t.properties.isCustomizable);
        });
      }

      // KAN-523 AC-11: Capture baseline snapshot and mark clean on load
      this.savedLayoutSnapshot = serializeLayoutForDirtyCheck(this.dashboard);
      this.syncDirtyState();

      // Auto-select first item if exists
      if (this.dashboard.length > 0) {
        this.selectTile(this.dashboard[0]);
      }

      this.loadLayoutBackground();
      this.loadAllFrameGroups();
      this.loadAllTileThumbnails();
    } catch (error) {
      console.error('Error loading layout configuration:', error);
      alert('Failed to load layout configuration from cloud.');
    } finally {
      this.isLoading = false;
    }
  }

  async loadAllFrameGroups(): Promise<void> {
    this.isLoadingFrameGroups = true;
    try {
      // Extract all unique theme IDs (tile.id) currently in the layout to ensure perfect parsing
      const knownThemeIds = Array.from(new Set(this.dashboard.map(t => t.id)));

      let groups: any[] = [];

      if (this.isGlobal) {
        // Global editor: load only global frame groups
        groups = await this.photoboothService.getAllFrameGroups(true, this.deviceId, this.version, knownThemeIds);
        groups.forEach(g => {
          (g as any).isGlobalGroup = true;
          (g as any).isDevice = false;
        });
        this.deviceFrameGroupThemeIds = Array.from(new Set(groups.map(g => g.themeId).filter(Boolean)));
      } else if (this.overrideGlobalLayout) {
        // Standalone Device Mode: load ONLY this device's own frame groups for strict isolation
        const deviceGroups = await this.photoboothService.getAllFrameGroups(false, this.deviceId, this.version, knownThemeIds);
        deviceGroups.forEach(g => {
          (g as any).isDevice = true;
          (g as any).isGlobalGroup = false;
        });
        groups = deviceGroups;
        this.deviceFrameGroupThemeIds = Array.from(new Set(deviceGroups.map(g => g.themeId).filter(Boolean)));
      } else {
        // Standard Inherited Mode: load global frame groups and any device overrides
        const globalGroups = await this.photoboothService.getAllFrameGroups(true, this.deviceId, this.version, knownThemeIds);
        globalGroups.forEach(g => {
          (g as any).isGlobalGroup = true;
          (g as any).isDevice = false;
        });

        const deviceGroups = await this.photoboothService.getAllFrameGroups(false, this.deviceId, this.version, knownThemeIds);
        deviceGroups.forEach(g => {
          (g as any).isDevice = true;
          (g as any).isGlobalGroup = false;
        });
        groups = [...globalGroups, ...deviceGroups];
        this.deviceFrameGroupThemeIds = Array.from(new Set(deviceGroups.map(g => g.themeId).filter(Boolean)));
      }

      const groupedMap = new Map<string, FrameGroupOption[]>();
      groups.forEach(g => {
        const groupLabel = (g as any).isDevice ? `device - ${g.themeId}` : g.themeId;
        if (!groupedMap.has(groupLabel)) {
          groupedMap.set(groupLabel, []);
        }
        groupedMap.get(groupLabel)!.push({
          themeId: g.themeId,
          frameId: g.frameId,
          displayName: g.name || g.frameId,
          isGlobalGroup: (g as any).isGlobalGroup
        });
      });

      this.allFrameGroupsGrouped = Array.from(groupedMap.keys()).map(groupLabel => {
        const options = groupedMap.get(groupLabel)!;
        const actualThemeId = options.length > 0 ? options[0].themeId : groupLabel;
        return {
          themeId: groupLabel, // Used for UI display label
          frames: options,
          expanded: this.activeItem?.properties?.tileType === 'FeaturedFrame' && this.activeItem?.properties?.featuredThemeId === actualThemeId
        };
      });

    } catch (error) {
      console.error('Error loading all frame groups:', error);
    } finally {
      this.isLoadingFrameGroups = false;
    }
  }

  async deleteFrameGroup(opt: FrameGroupOption, event: Event): Promise<void> {
    event.stopPropagation();
    if (confirm(`Are you sure you want to completely delete "${opt.displayName}" and all its assets from this machine?`)) {
      this.isLoadingFrameGroups = true;
      try {
        await this.photoboothService.deleteFrameGroupAndAssets(this.isGlobal, this.deviceId, opt.themeId, opt.frameId);
        await this.loadAllFrameGroups();

        // If the currently selected frame group is deleted, unselect it
        if (this.activeItem && this.activeItem.properties.featuredThemeId === opt.themeId && this.activeItem.properties.featuredFrameId === opt.frameId) {
          this.activeItem.properties.featuredThemeId = '';
          this.activeItem.properties.featuredFrameId = '';
          this.syncDirtyState();
        }
      } catch (error) {
        console.error('Error deleting frame group:', error);
        alert('Failed to delete frame group.');
      } finally {
        this.isLoadingFrameGroups = false;
      }
    }
  }

  addTile(): void {
    if (this.version || !this.isFullEditorUnlocked) return;
    const newId = `theme_${Date.now()}`;
    const newUid = `uid_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
    const newItem: ThemeSelectionTile = {
      id: newId,
      uid: newUid,
      x: 100,
      y: 100,
      cols: 400,
      rows: 600,
      properties: {
        title: 'New Selection',
        subTitle: 'Select this package',
        titleColor: '#ffffff',
        subtitleColor: '#e2e8f0',
        titleFontSize: 18,
        subtitleFontSize: 14,
        titleBold: false,
        titleItalic: false,
        titleUnderline: false,
        subtitleBold: false,
        subtitleItalic: false,
        subtitleUnderline: false,
        price: 10.00,
        priceClassicStrip: 10.00,
        priceMultiCut: 10.00,
        cutStyleKey: '4x6 Portrait',
        imageDirectory: newId,
        tileType: 'Theme',
        isCustomizable: false,
        isCustomized: this.overrideGlobalLayout ? true : false
      }
    };

    this.dashboard.push(newItem);
    this.selectTile(newItem);
    this.syncDirtyState();
  }

  async removeTile(item: ThemeSelectionTile): Promise<void> {
    if (this.version || !this.isFullEditorUnlocked) return;

    // KAN-523 AC-3: In standalone mode, inspect tile's frame_groups and Storage files and show confirmation modal
    if (this.overrideGlobalLayout && !this.isGlobal) {
      try {
        const allThemeIds = Array.from(new Set(this.dashboard.map(t => t.id).filter(Boolean)));
        const counts = await this.photoboothService.inspectStandaloneTileAssets(this.deviceId, item.id, allThemeIds);
        this.deleteTileConfirmModal = {
          tile: item,
          frameGroupCount: counts.frameGroups,
          storageFileCount: counts.files,
          promptText: STANDALONE_HOUSEKEEPING_MESSAGES.deleteTileConfirm(
            counts.frameGroups,
            counts.files
          )
        };
        this.cdr.detectChanges();
      } catch (e) {
        console.error('Error inspecting standalone tile assets:', e);
      }
      return;
    }

    this.spliceTileFromDashboard(item);
  }

  cancelDeleteStandaloneTile(): void {
    if (this.isDeletingStandaloneTile) return;
    this.deleteTileConfirmModal = null;
    this.cdr.detectChanges();
  }

  async confirmDeleteStandaloneTile(): Promise<void> {
    if (!this.deleteTileConfirmModal || this.isDeletingStandaloneTile) return;
    const item = this.deleteTileConfirmModal.tile;
    const allThemeIds = Array.from(new Set(this.dashboard.map(t => t.id).filter(Boolean)));
    this.isDeletingStandaloneTile = true;
    this.cdr.detectChanges();
    try {
      await this.photoboothService.deleteStandaloneTileAssets(this.deviceId, item.id, allThemeIds);
      const updatedThumbs = { ...this.tileThumbnails };
      delete updatedThumbs[item.id];
      this.tileThumbnails = updatedThumbs;
      this.spliceTileFromDashboard(item);
      this.deleteTileConfirmModal = null;
      await this.loadAllFrameGroups();
    } catch (e) {
      console.error('Failed to delete standalone tile assets:', e);
      alert('Failed to delete tile assets.');
    } finally {
      this.isDeletingStandaloneTile = false;
      this.cdr.detectChanges();
    }
  }

  private spliceTileFromDashboard(item: ThemeSelectionTile): void {
    const index = this.dashboard.indexOf(item);
    if (index > -1) {
      this.dashboard.splice(index, 1);
      if (this.activeItem?.id === item.id) {
        if (this.dashboard.length > 0) {
          this.selectTile(this.dashboard[0]);
        } else {
          this.activeItem = null;
          this.thumbnailPreview = null;
        }
      }
      this.syncDirtyState();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
        return;
      }
      if (this.activeItem && !this.version && this.isFullEditorUnlocked) {
        event.preventDefault();
        this.removeTile(this.activeItem);
      }
    }
  }

  previousTileId: string = '';

  selectTile(item: ThemeSelectionTile): void {
    this.activeItem = item;
    this.previousTileId = item.id;
    this.tileIdArtworkBlockedMessage = null;
    this.checkDuplicateId();
    this.loadThemeThumbnail(item.id);

    if (item.properties.tileType === 'FeaturedFrame' && item.properties.featuredThemeId) {
      const group = this.allFrameGroupsGrouped.find(g => g.themeId === item.properties.featuredThemeId);
      if (group) {
        group.expanded = true;
      }
    }
  }

  /**
   * KAN-523 AC-4: A tile's ID is locked when it already has artwork (thumbnail or frame groups).
   * Tiles without artwork can be renamed freely.
   */
  hasTileArtwork(item: ThemeSelectionTile | null): boolean {
    if (!item) return false;
    const checkId = (this.activeItem === item && this.previousTileId) ? this.previousTileId : item.id;
    const activeThumbUrl = this.activeItem === item ? this.thumbnailPreview?.url : null;
    return tileHasArtwork(
      checkId,
      this.tileThumbnails,
      this.deviceFrameGroupThemeIds,
      activeThumbUrl
    );
  }

  duplicateTileIdError: string | null = null;

  onTileIdChange(newId: string): void {
    if (!this.activeItem) return;

    // KAN-523 AC-4: Block renaming a tile that already has artwork with the required message
    if (tileHasArtwork(
      this.previousTileId,
      this.tileThumbnails,
      this.deviceFrameGroupThemeIds,
      this.thumbnailPreview?.url
    )) {
      this.activeItem.id = this.previousTileId;
      this.tileIdArtworkBlockedMessage = STANDALONE_HOUSEKEEPING_MESSAGES.tileIdChangeBlocked;
      alert(STANDALONE_HOUSEKEEPING_MESSAGES.tileIdChangeBlocked);
      this.cdr.detectChanges();
      return;
    }

    this.tileIdArtworkBlockedMessage = null;
    if (!this.activeItem.properties.imageDirectory || this.activeItem.properties.imageDirectory === this.previousTileId) {
      this.activeItem.properties.imageDirectory = newId;
    }
    this.previousTileId = newId;
    this.checkDuplicateId();
    this.syncDirtyState();
  }

  checkDuplicateId(): void {
    this.duplicateTileIdError = null;
    if (this.activeItem) {
      const count = this.dashboard.filter(t => t.id === this.activeItem!.id).length;
      if (count > 1) {
        this.duplicateTileIdError = 'Tile ID must be unique.';
      }
    }
  }

  adjustFontSize(type: 'title' | 'subtitle', amount: number): void {
    if (!this.activeItem) return;
    if (this.version || (!this.isFullEditorUnlocked && !this.activeItem.properties.isCustomizable)) return;

    if (type === 'title') {
      const current = this.activeItem.properties.titleFontSize || 18;
      this.activeItem.properties.titleFontSize = Math.max(8, current + amount);
    } else {
      const current = this.activeItem.properties.subtitleFontSize || 14;
      this.activeItem.properties.subtitleFontSize = Math.max(8, current + amount);
    }
    this.syncDirtyState();
  }

  toggleTextStyle(type: 'title' | 'subtitle', style: 'bold' | 'italic' | 'underline'): void {
    if (!this.activeItem) return;
    if (this.version || (!this.isFullEditorUnlocked && !this.activeItem.properties.isCustomizable)) return;

    if (type === 'title') {
      if (style === 'bold') this.activeItem.properties.titleBold = !this.activeItem.properties.titleBold;
      if (style === 'italic') this.activeItem.properties.titleItalic = !this.activeItem.properties.titleItalic;
      if (style === 'underline') this.activeItem.properties.titleUnderline = !this.activeItem.properties.titleUnderline;
    } else {
      if (style === 'bold') this.activeItem.properties.subtitleBold = !this.activeItem.properties.subtitleBold;
      if (style === 'italic') this.activeItem.properties.subtitleItalic = !this.activeItem.properties.subtitleItalic;
      if (style === 'underline') this.activeItem.properties.subtitleUnderline = !this.activeItem.properties.subtitleUnderline;
    }
    this.syncDirtyState();
  }

  onFeaturedFrameGroupChange(value: string): void {
    if (this.activeItem && value) {
      const parts = value.split('|');
      if (parts.length === 2) {
        this.activeItem.properties.featuredThemeId = parts[0];
        this.activeItem.properties.featuredFrameId = parts[1];
      } else {
        this.activeItem.properties.featuredThemeId = '';
        this.activeItem.properties.featuredFrameId = '';
      }
      this.syncDirtyState();
    }
  }

  trackByFrameOption(index: number, option: FrameGroupOption): string {
    return option.frameId;
  }

  async saveIsTesting(): Promise<void> {
    try {
      await this.storeService.updateDeviceTestingStatus(this.deviceId, this.isTesting);
      this.isTestingSaved = true;
      setTimeout(() => {
        this.isTestingSaved = false;
        this.cdr.detectChanges();
      }, 2000);
    } catch (e) {
      console.error('Failed to update isTesting', e);
      alert('Failed to update testing status.');
      this.isTesting = !this.isTesting; // revert on failure
    }
  }

  selectFrameGroup(opt: FrameGroupOption): void {
    if (this.version || (!this.isFullEditorUnlocked && !this.activeItem?.properties.isCustomizable)) return;

    if (this.activeItem) {
      this.activeItem.properties.featuredThemeId = opt.themeId;
      this.activeItem.properties.featuredFrameId = opt.frameId;
      this.syncDirtyState();
    }
  }

  async loadThemeThumbnail(themeId: string) {
    if (!themeId) {
      this.thumbnailPreview = null;
      return;
    }
    this.isLoadingThumbnail = true;
    try {
      this.thumbnailPreview = await this.photoboothService.getThemeThumbnail(this.isGlobal, this.deviceId, themeId, this.version);
      if (this.thumbnailPreview?.url) {
        this.tileThumbnails = {
          ...this.tileThumbnails,
          [themeId]: this.thumbnailPreview.url
        };
      } else {
        const updated = { ...this.tileThumbnails };
        delete updated[themeId];
        this.tileThumbnails = updated;
      }
    } catch (e) {
      console.error(e);
      this.thumbnailPreview = null;
      const updated = { ...this.tileThumbnails };
      delete updated[themeId];
      this.tileThumbnails = updated;
    } finally {
      this.isLoadingThumbnail = false;
      this.cdr.detectChanges();
    }
  }

  async loadAllTileThumbnails(): Promise<void> {
    const newThumbnails: { [tileId: string]: string } = {};
    const promises = this.dashboard.map(async (tile) => {
      const lookupIds = [tile.id, tile.properties?.imageDirectory, tile.properties?.featuredThemeId].filter(Boolean) as string[];
      for (const id of lookupIds) {
        try {
          const thumb = await this.photoboothService.getThemeThumbnail(this.isGlobal, this.deviceId, id, this.version);
          if (thumb?.url) {
            newThumbnails[tile.id] = thumb.url;
            break;
          }
        } catch (e) {
          // Ignore
        }
      }
    });
    await Promise.all(promises);
    this.tileThumbnails = newThumbnails;
    this.cdr.detectChanges();
  }

  async loadLayoutBackground() {
    this.isLoadingBackground = true;
    try {
      this.backgroundPreview = await this.photoboothService.getLayoutBackground(this.isGlobal, this.deviceId, this.version);
    } catch (e) {
      console.error(e);
      this.backgroundPreview = null;
    } finally {
      this.isLoadingBackground = false;
    }
  }

  async copyFromGlobal(): Promise<void> {
    if (confirm('This will overwrite the current device layout with the global layout. Continue?')) {
      this.isLoading = true;
      try {
        const rawTiles = await this.photoboothService.getGlobalLayout();
        if (rawTiles && rawTiles.length > 0) {
          this.dashboard = this.normalizeTiles(rawTiles);
          this.originalTileUids = new Set(this.dashboard.map(t => t.uid as string));
          if (this.dashboard.length > 0) {
            this.selectTile(this.dashboard[0]);
          } else {
            this.activeItem = null;
          }
          this.syncDirtyState();
          this.loadAllTileThumbnails();
          alert('Global Layout loaded successfully. Make sure to save changes.');
        } else {
          alert('No Global Layout configuration exists yet.');
        }
      } catch (error) {
        console.error('Error copying global layout:', error);
        alert('Failed to copy global layout.');
      } finally {
        this.isLoading = false;
      }
    }
  }

  openGlobalCopyModal(): void {
    this.isGlobalCopyModalOpen = true;
  }

  closeGlobalCopyModal(): void {
    if (!this.isCopyingGlobalGroup) {
      this.isGlobalCopyModalOpen = false;
    }
  }

  async onCopyGlobalGroup(event: { themeId: string, frameId: string }) {
    if (!this.activeItem || !this.activeItem.properties.isCustomizable) return;

    this.isCopyingGlobalGroup = true;
    try {
      this.activeItem.properties.featuredThemeId = event.themeId;
      this.activeItem.properties.featuredFrameId = event.frameId;
      this.syncDirtyState();
      this.isGlobalCopyModalOpen = false;
    } catch (error: any) {
      console.error('Error copying frame group', error);
      alert('Failed to copy frame group from global: ' + (error.message || 'Unknown error'));
    } finally {
      this.isCopyingGlobalGroup = false;
    }
  }

  async onCopyFromVersionChange(event: any): Promise<void> {
    const versionId = event.target.value;
    if (!versionId) return;

    const versionObj = this.publishedVersions.find(v => v.id === versionId);
    const versionNum = versionObj ? versionObj.version : versionId;

    if (confirm(`This will overwrite your current draft with the layout from published v${versionNum}. Continue?`)) {
      this.isLoading = true;
      try {
        let rawTiles: any[];
        if (this.isGlobal) {
          rawTiles = await this.photoboothService.getGlobalLayout(versionId);
        } else {
          rawTiles = await this.photoboothService.getDeviceLayout(this.deviceId, versionId);
        }

        if (rawTiles && rawTiles.length > 0) {
          this.dashboard = this.normalizeTiles(rawTiles);
          this.originalTileUids = new Set(this.dashboard.map(t => t.uid as string));
          if (this.dashboard.length > 0) {
            this.selectTile(this.dashboard[0]);
          } else {
            this.activeItem = null;
          }
          this.syncDirtyState();
          this.loadAllTileThumbnails();
          alert(`Layout copied from v${versionNum}. Make sure to click Save to keep these changes.`);
        } else {
          alert(`Version ${versionNum} has no layout tiles to copy.`);
        }
      } catch (error) {
        console.error('Error copying from version:', error);
        alert('Failed to copy from version.');
      } finally {
        this.isLoading = false;
      }
    }

    // Reset dropdown back to default
    event.target.value = "";
  }

  async saveLayout(): Promise<void> {
    if (this.dashboard.some(t => this.dashboard.filter(x => x.id === t.id).length > 1)) {
      alert('Cannot save: There are duplicate Tile IDs. Please make them unique.');
      return;
    }

    this.isLoading = true;
    try {
      const cleanTiles = this.dashboard.map(tile => {
        if (!tile.uid) {
          tile.uid = `uid_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
        }
        return {
          id: tile.id || '',
          uid: tile.uid || '',
          x: tile.x ?? 0,
          y: tile.y ?? 0,
          cols: tile.cols ?? 1,
          rows: tile.rows ?? 1,
          properties: {
            title: tile.properties.title || '',
            subTitle: tile.properties.subTitle || '',
            price: Number(tile.properties.price) || 0,
            priceClassicStrip: Number(tile.properties.priceClassicStrip) || 0,
            priceMultiCut: Number(tile.properties.priceMultiCut) || 0,
            cutStyleKey: tile.properties.cutStyleKey || '',
            imageDirectory: tile.properties.imageDirectory || (tile.properties as any).imageFileName || '',
            tileType: tile.properties.tileType || 'Theme',
            isCustomizable: this.overrideGlobalLayout ? true : !!tile.properties.isCustomizable,
            isCustomized: !!tile.properties.isCustomized,
            featuredThemeId: tile.properties.featuredThemeId || '',
            featuredFrameId: tile.properties.featuredFrameId || '',
            textPositionX: tile.properties.textPositionX || 0,
            textPositionY: tile.properties.textPositionY || 0,
            titleColor: tile.properties.titleColor || '#ffffff',
            subtitleColor: tile.properties.subtitleColor || '#e2e8f0',
            titleFontSize: tile.properties.titleFontSize || 18,
            subtitleFontSize: tile.properties.subtitleFontSize || 14,
            titleBold: !!tile.properties.titleBold,
            titleItalic: !!tile.properties.titleItalic,
            titleUnderline: !!tile.properties.titleUnderline,
            subtitleBold: !!tile.properties.subtitleBold,
            subtitleItalic: !!tile.properties.subtitleItalic,
            subtitleUnderline: !!tile.properties.subtitleUnderline
          }
        };
      });

      // Strip any lingering undefined values to prevent Firebase errors
      const safeTiles = JSON.parse(JSON.stringify(cleanTiles));

      if (this.isGlobal) {
        const disabledThemeIds: string[] = [];
        this.dashboard.forEach(t => {
          if (this.originalCustomizableState.get(t.id) === true && !t.properties.isCustomizable) {
            disabledThemeIds.push(t.id);
          }
        });

        // Confirms (count of inheriting machines), saves, cleans inheriting devices only, reports (KAN-519)
        const saveOutcome = await this.photoboothService.saveGlobalLayoutWithCustomizableOff(safeTiles, disabledThemeIds, {
          confirm: (m: string) => confirm(m),
          alert: (m: string) => alert(m)
        });
        if (saveOutcome === 'cancelled') {
          this.isLoading = false;
          return;
        }

        if (disabledThemeIds.length > 0) {
          this.dashboard.forEach(t => {
            this.originalCustomizableState.set(t.id, !!t.properties.isCustomizable);
          });
        }
      } else if (this.overrideGlobalLayout) {
        // Standalone Device Mode: Save entire canvas layout directly without global merging
        await this.photoboothService.saveDeviceLayout(this.deviceId, safeTiles, true);
      } else {
        const customizedTilesOnly = safeTiles.filter((t: any) => t.properties?.isCustomized).map((t: any) => ({
          id: t.id,
          uid: t.uid,
          x: t.x !== undefined ? t.x : 100,
          y: t.y !== undefined ? t.y : 100,
          cols: t.cols !== undefined ? t.cols : 400,
          rows: t.rows !== undefined ? t.rows : 600,
          properties: t.properties
        }));
        await this.photoboothService.saveDeviceLayout(this.deviceId, customizedTilesOnly, false);
      }
      this.originalTileUids = new Set(this.dashboard.map(t => t.uid as string));
      this.savedLayoutSnapshot = serializeLayoutForDirtyCheck(this.dashboard);
      this.syncDirtyState();

      alert('Layout configuration successfully saved to Cloud Firestore!');
    } catch (error) {
      console.error('Error saving layout config:', error);
      alert('Failed to save layout configuration.');
    } finally {
      this.isLoading = false;
    }
  }

  // KAN-523 AC-11: Publish to Device with unsaved-changes check
  async publishToDevice(): Promise<void> {
    if (this.isGlobal || !this.deviceId || this.version) return;
    this.syncDirtyState();
    if (this.hasUnsavedChanges) {
      this.isUnsavedPublishModalOpen = true;
      this.cdr.detectChanges();
      return;
    }
    if (!confirm(`Are you sure you want to publish the current configuration directly to ${this.deviceId}?`)) {
      return;
    }
    await this.executePublishToDevice();
  }

  cancelUnsavedPublishModal(): void {
    this.isUnsavedPublishModalOpen = false;
    this.cdr.detectChanges();
  }

  async confirmUnsavedPublishModal(): Promise<void> {
    this.isUnsavedPublishModalOpen = false;
    await this.executePublishToDevice();
  }

  private async executePublishToDevice(): Promise<void> {
    if (this.isGlobal || !this.deviceId || this.version) return;
    this.isPublishingDevice = true;
    this.cdr.detectChanges();
    try {
      await this.photoboothService.publishDeviceConfig(this.deviceId);
      alert(`Successfully published to ${this.deviceId}!`);
    } catch (error) {
      console.error('Publishing device failed', error);
      alert('Failed to publish to device. See console for details.');
    } finally {
      this.isPublishingDevice = false;
      this.cdr.detectChanges();
    }
  }

  goBack(): void {
    this.syncDirtyState();
    const queryParams = this.version ? { version: this.version } : {};
    const targetStore = this.isGlobal ? 'global' : this.storeId;
    const targetDevice = this.isGlobal ? 'global' : this.deviceId;
    this.router.navigate(['/home/photobooth-management/config', targetStore, targetDevice], { queryParams });
  }

  openFramesEditor(): void {
    if (this.activeItem && (this.activeItem.properties.tileType === 'Theme' || this.activeItem.properties.tileType === 'Signature' || this.activeItem.properties.tileType === 'FeaturedFrame')) {
      this.isFramesEditorOpen = true;
    }
  }

  closeFramesEditor(): void {
    this.isFramesEditorOpen = false;
    this.loadAllFrameGroups();
  }

  async uploadThumbnail(event: any): Promise<void> {
    const file: File = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.png')) {
      alert('Only PNG files are allowed.');
      return;
    }

    if (!this.activeItem?.id) {
      alert('Please define a Tile ID / Key first.');
      return;
    }

    this.isUploadingThumbnail = true;
    try {
      const storagePath = await this.photoboothService.uploadThemeThumbnail(this.isGlobal, this.deviceId, this.activeItem.id, file);

      alert(`Thumbnail successfully uploaded to ${storagePath}`);
      await this.loadThemeThumbnail(this.activeItem.id);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert('Failed to upload thumbnail.');
    } finally {
      this.isUploadingThumbnail = false;
      event.target.value = '';
    }
  }

  async uploadBackground(event: any): Promise<void> {
    const file: File = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.png')) {
      alert('Only PNG files are allowed.');
      return;
    }

    this.isUploadingBackground = true;
    try {
      const storagePath = await this.photoboothService.uploadLayoutBackground(this.isGlobal, this.deviceId, file);

      alert(`Background successfully uploaded to ${storagePath}`);
      await this.loadLayoutBackground();
    } catch (error) {
      console.error('Error uploading background:', error);
      alert('Failed to upload background.');
    } finally {
      this.isUploadingBackground = false;
      event.target.value = '';
    }
  }

  async removeThumbnail(): Promise<void> {
    if (!this.activeItem?.id) return;
    if (!confirm('Are you sure you want to remove the theme thumbnail?')) return;

    this.isUploadingThumbnail = true;
    try {
      await this.photoboothService.deleteThemeThumbnail(this.isGlobal, this.deviceId, this.activeItem.id);
      this.thumbnailPreview = null;
      const updated = { ...this.tileThumbnails };
      delete updated[this.activeItem.id];
      this.tileThumbnails = updated;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error removing thumbnail:', error);
      alert('Failed to remove thumbnail.');
    } finally {
      this.isUploadingThumbnail = false;
    }
  }

  async removeBackground(): Promise<void> {
    if (!confirm('Are you sure you want to remove the layout background?')) return;

    this.isUploadingBackground = true;
    try {
      await this.photoboothService.deleteLayoutBackground(this.isGlobal, this.deviceId);
      this.backgroundPreview = null;
    } catch (error) {
      console.error('Error removing background:', error);
      alert('Failed to remove background.');
    } finally {
      this.isUploadingBackground = false;
    }
  }

  isExistingTile(item: ThemeSelectionTile | null): boolean {
    if (!item || !item.uid) return false;
    return this.originalTileUids.has(item.uid);
  }

  getDragPosition(item: ThemeSelectionTile) {
    const el = document.querySelector('.viewport-inner-16-9');
    const scale = el ? el.clientWidth / 1920 : 1;
    return {
      x: (item.properties.textPositionX || 0) * scale,
      y: (item.properties.textPositionY || 0) * scale
    };
  }

  onTextDragEnded(event: CdkDragEnd, item: ThemeSelectionTile) {
    const transform = event.source.getFreeDragPosition();
    const el = document.querySelector('.viewport-inner-16-9');
    const scale = el ? 1920 / el.clientWidth : 1;

    item.properties.textPositionX = transform.x * scale;
    item.properties.textPositionY = transform.y * scale;
    this.syncDirtyState();
  }

  // --- Video & Printing Background Modal Logic ---

  extractVideoName(url: string): string {
    if (!url) return 'Unknown Video';
    try {
      const urlObj = new URL(url);
      let path = decodeURIComponent(urlObj.pathname);
      let filename = path.split('/').pop() || 'Unknown Video';

      // Remove timestamp (e.g., 1784558915713_video.mp4)
      if (filename.includes('_')) {
        const parts = filename.split('_');
        // If the first part is a long number (like Date.now()), remove it
        if (!isNaN(Number(parts[0])) && parts[0].length >= 10) {
          parts.shift();
          filename = parts.join('_');
        }
      }
      return filename;
    } catch (e) {
      return 'Unknown Video';
    }
  }

  async openVideoModal() {
    this.isVideoModalOpen = true;
    await Promise.all([
      this.loadVideos(),
      this.loadPrintingBackground(),
      this.loadExcludeGlobalVideos()
    ]);
  }

  closeVideoModal() {
    this.isVideoModalOpen = false;
  }

  async loadExcludeGlobalVideos() {
    if (this.isGlobal) {
      this.excludeGlobalVideos = false;
      return;
    }
    try {
      this.excludeGlobalVideos = await this.photoboothService.getExcludeGlobalVideos(this.isGlobal, this.deviceId, this.version);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading excludeGlobalVideos:', error);
    }
  }

  async toggleExcludeGlobalVideos() {
    if (this.isGlobal || this.version || this.isUpdatingExcludeGlobalVideos) return;
    this.isUpdatingExcludeGlobalVideos = true;
    const newValue = !this.excludeGlobalVideos;
    try {
      await this.photoboothService.setExcludeGlobalVideos(this.deviceId, newValue);
      this.excludeGlobalVideos = newValue;
    } catch (error) {
      console.error('Error setting excludeGlobalVideos:', error);
      alert('Failed to update global videos setting.');
    } finally {
      this.isUpdatingExcludeGlobalVideos = false;
      this.cdr.detectChanges();
    }
  }

  async loadVideos() {
    try {
      this.introVideos = await this.photoboothService.getVideos(this.isGlobal, this.deviceId, 'intro', this.version);
      this.printingVideos = await this.photoboothService.getVideos(this.isGlobal, this.deviceId, 'printing', this.version);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  }

  async loadPrintingBackground() {
    this.isLoadingPrintingBackground = true;
    try {
      this.printingBackgroundPreview = await this.photoboothService.getPrintingBackground(this.isGlobal, this.deviceId, this.version);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading printing background:', error);
    } finally {
      this.isLoadingPrintingBackground = false;
      this.cdr.detectChanges();
    }
  }

  async uploadPrintingBackground(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    this.isUploadingPrintingBackground = true;
    try {
      await this.photoboothService.uploadPrintingBackground(this.isGlobal, this.deviceId, files[0]);
      await this.loadPrintingBackground();
    } catch (error) {
      console.error('Error uploading printing background:', error);
      alert('Failed to upload printing background image.');
    } finally {
      this.isUploadingPrintingBackground = false;
      this.cdr.detectChanges();
      event.target.value = '';
    }
  }

  async deletePrintingBackground() {
    if (!confirm('Are you sure you want to delete the printing background image?')) return;

    this.isUploadingPrintingBackground = true;
    try {
      await this.photoboothService.deletePrintingBackground(this.isGlobal, this.deviceId);
      this.printingBackgroundPreview = null;
    } catch (error) {
      console.error('Error deleting printing background:', error);
      alert('Failed to delete printing background image.');
    } finally {
      this.isUploadingPrintingBackground = false;
      this.cdr.detectChanges();
    }
  }

  async uploadVideo(event: any, type: 'intro' | 'printing') {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    this.isUploadingVideo = true;
    try {
      await this.photoboothService.uploadVideo(this.isGlobal, this.deviceId, type, files[0]);
      await this.loadVideos();
    } catch (error) {
      console.error(`Error uploading ${type} video:`, error);
      alert(`Failed to upload ${type} video.`);
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
      event.target.value = '';
    }
  }

  async deleteVideo(type: 'intro' | 'printing', videoUrl: string) {
    if (!confirm(deleteConfirmationFor(classifyStorageUrl(videoUrl, this.isGlobal, this.deviceId)))) return;

    this.isUploadingVideo = true;
    try {
      const outcome = await this.photoboothService.deleteVideo(this.isGlobal, this.deviceId, type, videoUrl);
      await this.loadVideos();
      if (outcome === 'reference-only') alert(STORAGE_DELETE_MESSAGES.resultReferenceRemoved);
    } catch (error) {
      console.error(`Error deleting ${type} video:`, error);
      alert(`Failed to delete ${type} video.`);
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
    }
  }
}
